import {
  colors,
  containerNames,
  containers,
  darkThemeSelector,
  devices,
  rem,
  selectors,
  shadows,
  sizing,
  styled,
  UtilityControl,
  UtilityControlGroup,
  zIndexes,
} from '@meterup/atto';

const flexiColumnsZIndexes = {
  background: zIndexes.base + 1,
  foreground: zIndexes.base + 2,
  peek: zIndexes.base + 3,
  divider: zIndexes.base + 4,
};

export const FLEXI_COLUMNS_ANIMATION_MS = 200;

export const FLEXI_COLUMNS_RESIZE_BOUNDS = {
  min: 280,
  max: 720,
};

const flexiColumnsToggleButtonStyles = {
  minWidth: 0,
};

const opaqueControlStyles = {
  backgroundColor: colors.bgNeutralLight,

  [darkThemeSelector]: {
    backgroundColor: colors.bgNeutralDark,
  },
};

export const FlexiColumnsToggleButton = styled(UtilityControl, {
  position: 'absolute',

  [devices.touch]: {
    top: '$2',
  },

  [devices.mouse]: {
    top: '$8',
  },

  [containers.pane.notNarrow]: {
    transform: 'translateX(-50%)',
  },

  variants: {
    opaque: {
      true: {
        '&&': opaqueControlStyles,
      },
    },
    position: {
      start: {
        [containers.pane.narrow]: {
          left: 0,
          borderRadius: '0 $8 $8 0',
        },

        [containers.pane.notNarrow]: {
          left: `$${sizing.primary}`,
          transform: 'translateX(-50%)',
        },
      },
      end: {
        [containers.pane.narrow]: {
          right: 0,
          borderRadius: '$8 0 0 $8',
        },

        [containers.pane.notNarrow]: {
          right: `$${sizing.primary}`,
          transform: 'translateX(50%)',
        },
      },
    },
  },

  compoundVariants: [
    {
      position: 'start',
      css: { ...flexiColumnsToggleButtonStyles },
    },
    {
      position: 'end',
      css: { ...flexiColumnsToggleButtonStyles },
    },
  ],
});
FlexiColumnsToggleButton.displayName = 'FlexiColumnsToggleButton';

const columnVisibleStyles = {
  zIndex: 2,

  [containers.pane.narrow]: {
    overflow: 'auto',
    transform: 'translateX(0)',
  },

  [containers.pane.notNarrow]: {
    minWidth: 0,
    overflow: 'auto',
  },
};

const columnHiddenStyles = {
  zIndex: flexiColumnsZIndexes.peek,

  [containers.pane.narrow]: {
    overflow: 'visible',
    boxShadow: shadows.overlayLight,
    transform: `translateX(calc(-100% + ${sizing.primary}px))`,

    [darkThemeSelector]: {
      boxShadow: shadows.overlayDark,
    },

    [selectors.focus]: {
      outline: 'none',
    },
  },

  [containers.pane.notNarrow]: {
    overflow: 'hidden',
    minWidth: 0,
  },
};

export const FlexiColumnsColumn = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'auto',
  background: colors.bgApplicationLight,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
  },

  [containers.pane.narrow]: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    transition: `transform ${FLEXI_COLUMNS_ANIMATION_MS}ms ease`,
    willChange: 'transform',
  },

  variants: {
    hasDivider: {
      true: {},
      false: {},
    },
    position: {
      start: {
        [containers.pane.notNarrow]: {
          gridArea: 'start',
          strokeRight: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            strokeRight: colors.strokeNeutralDark,
          },
        },

        [containers.pane.narrow]: {
          zIndex: flexiColumnsZIndexes.foreground,
        },
      },
      end: {
        [containers.pane.notNarrow]: {
          gridArea: 'end',
          strokeLeft: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            strokeLeft: colors.strokeNeutralDark,
          },
        },

        [containers.pane.narrow]: {
          zIndex: flexiColumnsZIndexes.background,
        },
      },
    },
    visible: {
      true: columnVisibleStyles,
      false: columnHiddenStyles,
    },
  },

  compoundVariants: [
    {
      position: 'start',
      visible: false,
      css: {
        [containers.pane.narrow]: {
          transform: 'translateX(-100%)',
        },
        [containers.pane.notNarrow]: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        },
      },
    },
    {
      position: 'end',
      visible: false,
      css: {
        [containers.pane.narrow]: {
          transform: 'translateX(100%)',
        },
      },
    },
    {
      hasDivider: true,
      position: 'start',
      css: {
        [containers.pane.notNarrow]: {
          strokeRight: colors.transparent,

          [darkThemeSelector]: {
            strokeRight: colors.transparent,
          },
        },
      },
    },
    {
      position: 'end',
      visible: false,
      css: {
        [containers.pane.notNarrow]: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        },
      },
    },
    {
      hasDivider: true,
      position: 'end',
      css: {
        [containers.pane.notNarrow]: {
          strokeLeft: colors.transparent,

          [darkThemeSelector]: {
            strokeLeft: colors.transparent,
          },
        },
      },
    },
  ],
});
FlexiColumnsColumn.displayName = 'FlexiColumnsColumn';

export const FlexiColumnsColumnBody = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  flex: 1,
});
FlexiColumnsColumnBody.displayName = 'FlexiColumnsColumnBody';

/**
 * Width of the divider track. Exported so `FlexiColumns` can subtract it when working out how wide
 * a column was before it collapsed.
 */
export const FLEXI_COLUMNS_DIVIDER_WIDTH = rem(1);

/**
 * Every selector below reaches its parts through `>`, without exception. A nested `FlexiColumns`
 * renders this same component, so `&` matches the outer container as much as the inner one — a
 * descendant combinator here would let whichever container is further out decide whether the inner
 * one shows its divider and its overlay, and the inner one's own state would count for nothing.
 */
export const FlexiColumnsContainer = styled('div', {
  position: 'relative',
  width: '100%',
  height: '100%',
  minHeight: '0',
  containerType: 'inline-size',
  containerName: containerNames.pane,

  [containers.pane.narrow]: {
    overflowX: 'hidden',
    gridTemplateColumns: '1fr',
    gridTemplateAreas: '"start"',

    '&[data-active-column="start"] > [data-position="start"]': {
      zIndex: 2,
      overflow: 'auto',
      transform: 'translateX(0)',
    },
    '&[data-active-column="start"] > [data-position="end"]': {
      zIndex: flexiColumnsZIndexes.background,
      overflow: 'visible',
      transform: 'translateX(100%)',
    },
    '&[data-active-column="end"] > [data-position="end"]': {
      zIndex: 2,
      overflow: 'auto',
      transform: 'translateX(0)',
    },
    '&[data-active-column="end"] > [data-position="start"]': {
      zIndex: flexiColumnsZIndexes.foreground,
      overflow: 'visible',
      transform: 'translateX(-100%)',
    },

    '& > [data-flexi-overlay]': {
      display: 'block',
    },
    '& > [data-flexi-divider]': {
      display: 'none',
    },
  },

  [containers.pane.notNarrow]: {
    display: 'grid',
    gridTemplateRows: '1fr',
  },

  '&[data-both-panes-visible="true"][data-resizable="true"]': {
    [containers.pane.notNarrow]: {
      gridTemplateAreas: '"start divider end"',

      '&[data-preset="narrow-wide"]': {
        gridTemplateColumns: `var(--flexi-start-width) ${FLEXI_COLUMNS_DIVIDER_WIDTH} 1fr`,
      },
      '&[data-preset="wide-narrow"]': {
        gridTemplateColumns: `1fr ${FLEXI_COLUMNS_DIVIDER_WIDTH} var(--flexi-start-width)`,
      },
      '&[data-preset="even-split"]': {
        gridTemplateColumns: `var(--flexi-start-fr) ${FLEXI_COLUMNS_DIVIDER_WIDTH} var(--flexi-end-fr)`,
      },
    },
  },

  '&[data-both-panes-visible="true"][data-resizable="false"]': {
    [containers.pane.notNarrow]: {
      gridTemplateAreas: '"start divider end"',

      '&[data-preset="narrow-wide"]': {
        gridTemplateColumns: `var(--flexi-fixed-start, $400) ${FLEXI_COLUMNS_DIVIDER_WIDTH} 1fr`,
      },
      '&[data-preset="wide-narrow"]': {
        gridTemplateColumns: `1fr ${FLEXI_COLUMNS_DIVIDER_WIDTH} var(--flexi-fixed-end, $400)`,
      },
      '&[data-preset="even-split"]': {
        gridTemplateColumns: `1fr ${FLEXI_COLUMNS_DIVIDER_WIDTH} 1fr`,
      },
    },
  },

  '&[data-end-overlay="true"][data-both-panes-visible="true"]': {
    [containers.pane.notNarrow]: {
      gridTemplateAreas: '"start"',
      gridTemplateColumns: '1fr',

      '& > [data-position="end"]': {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        zIndex: flexiColumnsZIndexes.foreground,
        width: 'min(var(--flexi-end-overlay-width), 100%)',
      },
      '& > [data-flexi-divider]': {
        position: 'absolute',
        top: 0,
        right: 'min(var(--flexi-end-overlay-width), 100%)',
        bottom: 0,
      },
    },
  },

  '&[data-both-panes-visible="false"]': {
    [containers.pane.notNarrow]: {
      gridTemplateAreas: '"start end"',

      '&[data-active-column="start"]': {
        gridTemplateColumns: '1fr var(--flexi-peek-width-end)',
      },
      '&[data-active-column="end"]': {
        gridTemplateColumns: 'var(--flexi-peek-width-start) 1fr',
      },

      // The collapsed column holds the width it had before it collapsed, so the peek shows a slice
      // of the layout the user was just looking at. Left to size itself against the peek strip its
      // content reflows into a column a few characters wide — rows wrap, labels truncate — and the
      // sliver on show is of a layout that never existed. `max-content` isn't a substitute: content
      // that truncates or sets `min-width: 0` contributes almost nothing to it, so the very rows
      // most likely to reflow are the ones it fails to hold open.
      '&[data-active-column="end"] > [data-position="start"] > [data-flexi-slot="body"]': {
        width: 'var(--flexi-expanded-width-start)',
        minWidth: '100%',
      },
      '&[data-active-column="start"] > [data-position="end"] > [data-flexi-slot="body"]': {
        width: 'var(--flexi-expanded-width-end)',
        minWidth: '100%',
      },

      '& > [data-flexi-overlay]': {
        display: 'block',
      },
      '& > [data-flexi-divider]': {
        display: 'none',
      },
    },
  },

  '&[data-both-panes-visible="true"]': {
    [containers.pane.notNarrow]: {
      '& > [data-flexi-overlay]': {
        display: 'none',
      },
    },
  },

  '&[data-active-column="start"] > [data-flexi-overlay] [data-toggle="start"]': {
    display: 'none',
  },
  '&[data-active-column="end"] > [data-flexi-overlay] [data-toggle="end"]': {
    display: 'none',
  },
  '&[data-nested-active-column="end"] > [data-flexi-overlay] [data-toggle="start"]': {
    [containers.pane.narrow]: {
      display: 'none',
    },
  },
});
FlexiColumnsContainer.displayName = 'FlexiColumnsContainer';

export const FlexiColumnsOverlay = styled('div', {
  position: 'absolute',
  inset: 0,
  zIndex: flexiColumnsZIndexes.peek,
  pointerEvents: 'none',
  display: 'none',

  '& > *': {
    pointerEvents: 'auto',
  },
});
FlexiColumnsOverlay.displayName = 'FlexiColumnsOverlay';

export const FlexiColumnsControlsContainer = styled(UtilityControlGroup, {
  position: 'absolute',
  left: 0,
  transform: 'translateX(-50%)',

  [devices.touch]: {
    top: '$2',
  },

  [devices.mouse]: {
    top: '$8',
  },

  variants: {
    opaque: {
      true: {
        '&& > button': opaqueControlStyles,
      },
    },
  },
});
FlexiColumnsControlsContainer.displayName = 'FlexiColumnsControlsContainer';

export const FlexiColumnsDivider = styled('div', {
  gridArea: 'divider',
  position: 'relative',
  widthAll: FLEXI_COLUMNS_DIVIDER_WIDTH,
  zIndex: flexiColumnsZIndexes.divider,
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    backgroundColor: colors.strokeNeutralDark,
  },
});
FlexiColumnsDivider.displayName = 'FlexiColumnsDivider';
